import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { FieldsetModule } from 'primeng/fieldset';
import { forkJoin, of, Observable, Subscription, throwError } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { CommonModule } from '@angular/common';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { Business } from '../../master-data/business/business.model';
import { BusinessService } from '../../master-data/business/business.service';
import { CustomersService } from '../../master-data/customers/customers.service';
import { Customer } from '../../master-data/customers/customers.model';
import { AppUser } from '../../users/user.model';
import { ContractService } from '../contract.service';
import { DatePicker } from 'primeng/datepicker';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AuthenticationService } from '../../services/authentication.service';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { GENERAL_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { catchError, filter, map, take, switchMap } from 'rxjs/operators';
import { ContractAttachment } from '../contract-attachment.model';

@Component({
  selector: 'app-add-edit-contract',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    CalendarModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    FieldsetModule,
    ButtonModule,
    IftaLabelModule,
    FloatLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    FileUploadModule,
    ProgressBarModule,
    DatePicker,
    ProgressSpinner,
    ConfirmDialog,
  ],
  providers: [MessageService],
  templateUrl: './contract-sales-form.component.html',
  styleUrls: ['./contract-sales-form.component.css'],
})
export class ContractSalesFormComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly attachmentAcceptTypes = GENERAL_UPLOAD_ACCEPT_TYPES;
  contractForm!: FormGroup;
  isLoading = false;
  isUploadingAttachments = false;
  attachmentUploadProgress = 0;
  submitted = false;
  isEditMode = false;
  contractId: string | null = null;
  currentUser: AppUser;
  // Breadcrumb items
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  user: AppUser | null = null;
  user$: Observable<AppUser | null>;

  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  filteredSuppliers: Supplier[] = [];

  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];

  tomorrowDate!: Date;

  suppliersPerRow: Supplier[][] = [];
  oldSuppliersPerRow: Supplier[][] = [];

  CustomerfilterValue: string = '';
  BusinessfilterValue: string[] = [];
  SupplierfilterValue: string[] = [];
  private attachmentProgressByIndex = new Map<number, number>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private messageService: MessageService,
    private router: Router,
    private businessService: BusinessService,
    private customerService: CustomersService,
    private contractService: ContractService,
    private route: ActivatedRoute,
    private storageService: FirebaseStorageService
  ) {
    this.currentUser = JSON.parse(
      sessionStorage.getItem('appUser') ?? ''
    ) as AppUser;
    this.user$ = this.authService.getAuthState();
  }

  ngOnInit(): void {
    this.user$.subscribe((user) => {
      if (user) {
        this.user = user;
      } else {
        this.user = null;
      }
    });
    this.getTomorrowDate();

    this.contractId = this.route.snapshot.paramMap.get('contractId');
    this.isEditMode = !!this.contractId;

    this.initForm();
    // this.subscribeToValueChanges();

    // Load all necessary dropdown data and then proceed with form population
    this.loadDropdownData().subscribe({
      next: (data) => {
        if (this.user?.roles[0]?.roleName === 'Admin') {
          this.customers = data.customers.content;
          this.filteredCustomers = data.customers.content;
          this.businesses = data.businesses.content;
          this.filteredBusinesses = data.businesses.content;
        } else {
          if (!this.user?.customerIds || this.user.customerIds.length === 0) {
            this.customers = data.customers.content;
            this.filteredCustomers = data.customers.content;
          } else {
            this.customers = data.customers.content;
            this.filteredCustomers =
              this.user?.customerIds && this.user.customerIds.length > 0
                ? this.customers.filter((c) =>
                  this.user?.customerIds?.includes(c.id)
                )
                : [];
          }

          this.businesses = data.businesses.content;
          this.filteredBusinesses =
            this.user?.businessIds && this.user.businessIds.length > 0
              ? this.businesses.filter((b) =>
                this.user?.businessIds?.includes(b.id)
              )
              : [];
        }
        // Now that dropdowns are populated, we can safely check for pre-fill or edit mode.
        if (this.isEditMode) {
          this.contractService
            .getContractById(+this.contractId!)
            .subscribe((contract) => {
              const customerToSet = this.customers.find(
                (c) => c.id === contract.customer?.id
              );
              const businessToSet = this.businesses.find(
                (b) => b.id === contract.business?.id
              );
            });
        } else {
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load required form data.',
        });
        console.error('Error loading dropdown data:', err);
      },
    });
  }

  private initForm(): void {
    this.contractForm = this.fb.group({
      // Contract Request
      contractName: ['', Validators.required],
      startDateActive: ['', Validators.required],
      endDateActive: ['', Validators.required],
      customer: [[], Validators.required],
      // business: [null],
      contractNote1: [''],
      contractDetails: this.fb.array([], Validators.required), // form array
      attachments: this.fb.array([]),
    });
    // أول عنصر افتراضي
    this.addItemToContractDetail();
  }

  isInvalid(controlName: string): boolean {
    const control = this.contractForm.get(controlName);
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || this.submitted)
    );
  }
  isDetailsInvalid(index: number, controlName: string): boolean {
    const detailArray = this.contractForm.get('contractDetails') as FormArray;
    const control = detailArray.at(index).get(controlName);

    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || this.submitted)
    );
  }

  get contractDetails(): FormArray {
    return this.contractForm.get('contractDetails') as FormArray;
  }

  addItemToContractDetail() {
    const detail = this.fb.group({
      business: [null, Validators.required],
      vendor: [null, Validators.required],
      rebate: [
        null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      jbpPrc: [null, Validators.required],
      fixedJbp: [null, Validators.required],
      progressiveSlap1: [null, Validators.required],
      progressiveSlap1Prc: [null, [Validators.min(0), Validators.max(100)]],
      progressiveSlap2: [null, Validators.required],
      progressiveSlap2Prc: [null, [Validators.min(0), Validators.max(100)]],
      progressiveSlap3: [null, Validators.required],
      progressiveSlap3Prc: [null, [Validators.min(0), Validators.max(100)]],
      deliverables: ['', Validators.required],
    });
    this.contractDetails.push(detail);
  }

  removeItemFromContractDetail(index: number) {
    this.contractDetails.removeAt(index);
  }


  submitForm() {
    this.submitted = true;
    if (this.contractForm.valid) {
      const contractFormClone = this.contractForm.value;

      const formattedContract = {
        ...contractFormClone,
        startDateActive: this.formatDateToLocal(
          contractFormClone.startDateActive
        ),
        endDateActive: this.formatDateToLocal(contractFormClone.endDateActive),
      };
      delete (formattedContract as any).attachments;
      this.isLoading = true;

      this.contractService.createSalesContract(formattedContract).subscribe({
        next: (data) => {
          const contractId = data.contractId;
          if (!contractId) {
            this.isLoading = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Contract saved, but the contract ID was not returned for attachment upload.',
            });
            console.error('createSalesContract did not return a contractId:', data);
            return;
          }

          this.uploadContractAttachments(contractId).subscribe({
            next: () => {
              this.isLoading = false;
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Contract saved successfully.',
              });
              setTimeout(
                () =>
                  this.router.navigate([
                    '/ai-studio/session/current',
                    { outlets: { canvasOutlet: ['rebate', 'list'] } },
                  ]),
                1500
              );
            },
            error: (err) => {
              this.isLoading = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Contract saved, but attachment upload failed.',
              });
              console.error('Attachment upload failed after contract save:', err);
            }
          });
        },
        error: (err) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save contract.',
          });
          console.error('Error saving contract:', err);
        },
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields.',
      });
    }
  }

  getTomorrowDate() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.tomorrowDate = tomorrow;
  }

  onCancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['rebate', 'list'] } },
    ]);
  }

  private loadDropdownData() {
    return forkJoin({
      customers: this.customerService.getAllCustomers({
        page: 0,
        size: 10000, //2000
        loadLocations: false,
      }),
      businesses: this.businessService.getAllBusinesss({
        page: 0,
        size: 10000,
        loadSuppliers: true,
      }),
    });
  }

  filterCustomers(event: any) {
    this.CustomerfilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredCustomers = this.customers;
    } else {
      this.filteredCustomers = this.customers.filter(
        (customer) =>
          customer.customerName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          customer.customerNumber
            .toLowerCase()
            .includes(event.target.value.toLowerCase())
      );
    }
    if (this.filteredCustomers.length === 1) {
      this.contractForm.get('customer')?.setValue(this.filteredCustomers[0]);
    }
  }

  filterBusinesses(event: any, index: number) {
    this.BusinessfilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredBusinesses = this.businesses;
    } else {
      this.filteredBusinesses = this.businesses.filter(
        (business) =>
          business.businessName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (business.businessCode &&
            business.businessCode
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  filterSuppliers(event: any, index: number) {
    this.SupplierfilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.suppliersPerRow[index] = this.oldSuppliersPerRow[index];
    } else {
      this.suppliersPerRow[index] = this.suppliersPerRow[index].filter(
        (supplier) =>
          supplier.supplierName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (supplier.supplierNumber &&
            supplier.supplierNumber
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  ngOnDestroy(): void { }

  get attachments(): FormArray {
    return this.contractForm.get('attachments') as FormArray;
  }

  onFileSelect(event: any): void {
    const files: File[] = Array.isArray(event.files)
      ? event.files
      : Array.from(event.files ?? []);
    if (files.length === 0) {
      return;
    }

    files.forEach(file => {
      this.attachments.push(
        this.fb.group({
          attachmentId: [null],
          fileName: [file.name],
          fileType: [file.type],
          fileSize: [file.size],
          fileUrl: [''],
          file: [file],
        })
      );
    });
  }

  removeAttachment(index: number): void {
    this.attachments.removeAt(index);
  }

  private uploadContractAttachments(contractId: number) {
    const pendingAttachments = this.attachments.controls
      .filter(control => !control.get('attachmentId')?.value && (control.get('file')?.value || control.get('fileUrl')?.value))
      .map(control => ({
        file: control.get('file')?.value as File | null,
        fileName: control.get('fileName')?.value as string,
        fileType: control.get('fileType')?.value as string,
        fileSize: control.get('fileSize')?.value as number,
        fileUrl: control.get('fileUrl')?.value as string,
      }));

    if (pendingAttachments.length === 0) {
      return of([]);
    }

    this.isUploadingAttachments = true;
    this.attachmentUploadProgress = 0;
    this.attachmentProgressByIndex.clear();
    const contractFolder = `contracts/${contractId}`;

      return forkJoin(
      pendingAttachments.map((item, index) => {
        if (item.fileUrl) {
          return of({
            fileName: item.fileName,
            fileType: item.fileType,
            fileSize: item.fileSize,
            fileUrl: item.fileUrl,
            uploadedBy: this.currentUser?.fullName || this.currentUser?.email || 'N/A',
          } as ContractAttachment);
        }

        const safeFileName = item.fileName.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const storagePath = `${contractFolder}/${Date.now()}-${safeFileName}`;
        return this.storageService.uploadFileWithProgress(item.file as File, storagePath).pipe(
          filter(event => {
            this.updateAttachmentProgress(index, event.progress, pendingAttachments.length);
            return !!event.downloadURL;
          }),
          take(1),
          map(event => ({
            fileName: item.fileName,
            fileType: item.fileType,
            fileSize: item.fileSize,
            fileUrl: event.downloadURL!,
            uploadedBy: this.currentUser?.fullName || this.currentUser?.email || 'N/A',
          } as ContractAttachment))
        );
      })
    ).pipe(
      switchMap(uploadedAttachments => {
        this.markUploadedAttachmentsAsStaged(uploadedAttachments);
        return this.contractService.uploadAttachments(contractId, uploadedAttachments);
      }),
      map(savedAttachments => {
        this.markUploadedAttachmentsAsSaved(savedAttachments);
        this.isUploadingAttachments = false;
        return savedAttachments;
      }),
      catchError((err) => {
        this.isUploadingAttachments = false;
        this.attachmentUploadProgress = 0;
        console.error('Error saving attachments for contract:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.getAttachmentSaveErrorMessage(err),
        });
        return throwError(() => err);
      })
    );
  }

  private markUploadedAttachmentsAsStaged(uploadedAttachments: ContractAttachment[]): void {
    uploadedAttachments.forEach((attachment) => {
      const control = this.attachments.controls.find((candidate) => {
        const fileName = candidate.get('fileName')?.value;
        const fileSize = candidate.get('fileSize')?.value;
        const fileType = candidate.get('fileType')?.value;
        return fileName === attachment.fileName
          && fileSize === attachment.fileSize
          && fileType === attachment.fileType;
      });

      if (control) {
        control.patchValue({
          fileUrl: attachment.fileUrl,
          file: null,
        });
      }
    });
  }

  private markUploadedAttachmentsAsSaved(savedAttachments: ContractAttachment[]): void {
    savedAttachments.forEach((attachment) => {
      const control = this.attachments.controls.find((candidate) => {
        const fileName = candidate.get('fileName')?.value;
        const fileUrl = candidate.get('fileUrl')?.value;
        return fileName === attachment.fileName && fileUrl === attachment.fileUrl;
      });

      if (control) {
        control.patchValue({
          attachmentId: attachment.attachmentId ?? null,
          fileUrl: attachment.fileUrl ?? '',
          file: null,
        });
      }
    });
  }

  private getAttachmentSaveErrorMessage(err: any): string {
    return err?.error?.message
      || err?.error?.detail
      || err?.message
      || 'Contract saved, but attachment upload failed.';
  }

  private updateAttachmentProgress(index: number, progress: number, totalFiles: number): void {
    this.attachmentProgressByIndex.set(index, progress);
    const totalProgress = Array.from(this.attachmentProgressByIndex.values()).reduce((sum, value) => sum + value, 0);
    this.attachmentUploadProgress = Math.round(totalProgress / Math.max(totalFiles, 1));
  }

  formatDateToLocal(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onBusinessChange(index: number, business: Business) {
    if (this.suppliersPerRow.length <= index) {
      this.suppliersPerRow.push([]);
    }
    this.suppliersPerRow[index] = business.suppliers ?? [];
    this.oldSuppliersPerRow[index] = business.suppliers ?? [];

    const detailArray = this.contractForm.get('contractDetails') as FormArray;
    detailArray.at(index).get('vendor')?.reset();

    if (this.suppliersPerRow[index].length === 1) {
      detailArray
        .at(index)
        .get('vendor')
        ?.setValue(this.suppliersPerRow[index][0]);
    }
  }
}
